import React from 'react';
import { Link } from 'react-router-dom';
import { Globe, ArrowRight, BookOpen, Clock, Tag } from 'lucide-react';
import Footer from './Footer';
import SEO from './SEO';

const BLOG_POSTS = [
    {
        slug: "pdf-nasil-cevirilir",
        title: "PDF Dosyası Nasıl Çevrilir? Adım Adım Rehber",
        summary: "PDF belgelerinizi hızlı ve doğru bir şekilde farklı dillere çevirmek için bilmeniz gereken her şey bu rehberde.",
        tag: "Rehber",
        readTime: "5 dk",
        date: "7 Mart 2025",
        color: "blue",
    },
    {
        slug: "ocr-teknolojisi-nedir",
        title: "OCR Teknolojisi Nedir? Taranmış PDF'ler Nasıl İşlenir?",
        summary: "Optik Karakter Tanıma (OCR) teknolojisi, görsel tabanlı belgeleri düzenlenebilir metne dönüştürür. Peki bu nasıl çalışır?",
        tag: "Teknoloji",
        readTime: "7 dk",
        date: "5 Mart 2025",
        color: "purple",
    },
    {
        slug: "dokuman-ceviri-ipuclari",
        title: "Doküman Çevirisinde Kaliteyi Artıracak 8 İpucu",
        summary: "İyi bir çeviri yalnızca dönüştürmekle bitmez. Orijinal anlamı, tonu ve yapıyı korumak için pratik ipuçları.",
        tag: "İpuçları",
        readTime: "6 dk",
        date: "2 Mart 2025",
        color: "indigo",
    },
    {
        slug: "epub-ceviri-rehberi",
        title: "E-Kitap (EPUB) Çevirisi: En İyi Uygulamalar",
        summary: "EPUB formatındaki e-kitapları çevirirken dikkat etmeniz gereken özel konular ve CeviriPro ile nasıl yapılacağı.",
        tag: "Rehber",
        readTime: "4 dk",
        date: "28 Şubat 2025",
        color: "green",
    },
];

const colorMap = {
    blue: "bg-blue-100 text-blue-700",
    purple: "bg-purple-100 text-purple-700",
    indigo: "bg-indigo-100 text-indigo-700",
    green: "bg-green-100 text-green-700",
};

const Blog = () => {
    return (
        <div className="min-h-screen bg-white">
            <SEO
                title="Blog — Çeviri Rehberleri ve Teknoloji Yazıları"
                description="CeviriPro Blog: PDF çeviri rehberleri, OCR teknolojisi, EPUB e-kitap çevirisi ve yapay zeka çeviri hakkında pratik ipuçları ve derinlikli yazılar."
                canonical="/blog"
            />
            {/* Nav */}
            <nav className="border-b border-slate-100 bg-white/80 backdrop-blur-sm sticky top-0 z-10">
                <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2">
                        <div className="bg-gradient-to-r from-blue-600 to-green-600 p-2 rounded-xl">
                            <Globe className="w-5 h-5 text-white" />
                        </div>
                        <span className="font-bold text-slate-800 text-lg">CeviriPro</span>
                    </Link>
                    <div className="flex items-center gap-4">
                        <Link to="/login" className="text-slate-600 hover:text-blue-900 text-sm font-medium transition-colors">Giriş Yap</Link>
                        <Link to="/register" className="px-4 py-2 bg-gradient-to-r from-blue-600 to-green-600 text-white text-sm font-semibold rounded-xl hover:shadow-lg transition-all">Ücretsiz Başla</Link>
                    </div>
                </div>
            </nav>

            {/* Hero */}
            <section className="bg-gradient-to-br from-blue-50 via-indigo-50 to-green-50 py-16 px-4 text-center">
                <div className="w-16 h-16 bg-white rounded-2xl shadow-md flex items-center justify-center mx-auto mb-6">
                    <BookOpen className="w-8 h-8 text-blue-600" />
                </div>
                <span className="inline-block px-4 py-1.5 bg-white border border-blue-200 text-blue-600 text-sm font-semibold rounded-full mb-4">Blog</span>
                <h1 className="text-4xl font-black text-slate-900 mb-4">Çeviri Dünyasından Haberler</h1>
                <p className="text-xl text-slate-600 max-w-xl mx-auto">Rehberler, teknoloji yazıları ve pratik ipuçları.</p>
            </section>

            {/* Blog Posts */}
            <section className="py-16 px-4">
                <div className="max-w-6xl mx-auto">
                    {/* Featured */}
                    <Link to={`/blog/${BLOG_POSTS[0].slug}`} className="block mb-12 bg-gradient-to-br from-blue-600 to-green-700 rounded-3xl p-8 md:p-12 text-white relative overflow-hidden group hover:shadow-2xl transition-all">
                        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2 pointer-events-none group-hover:scale-110 transition-transform"></div>
                        <span className="inline-block px-3 py-1 bg-white/20 text-white text-xs font-bold rounded-full mb-4 uppercase tracking-wider">Öne Çıkan</span>
                        <h2 className="text-2xl md:text-3xl font-black mb-4 leading-tight">{BLOG_POSTS[0].title}</h2>
                        <p className="text-blue-100 text-lg mb-6 max-w-2xl">{BLOG_POSTS[0].summary}</p>
                        <div className="flex items-center gap-4">
                            <span className="flex items-center gap-1.5 text-blue-200 text-sm"><Clock className="w-4 h-4" />{BLOG_POSTS[0].readTime} okuma</span>
                            <span className="text-blue-300">•</span>
                            <span className="text-blue-200 text-sm">{BLOG_POSTS[0].date}</span>
                        </div>
                    </Link>

                    {/* Grid */}
                    <div className="grid md:grid-cols-3 gap-6">
                        {BLOG_POSTS.slice(1).map((post) => (
                            <article key={post.slug} className="bg-white border border-slate-200 rounded-2xl overflow-hidden hover:shadow-lg hover:-translate-y-1 transition-all">
                                <div className="p-6">
                                    <div className="flex items-center gap-3 mb-4">
                                        <span className={`px-3 py-1 rounded-full text-xs font-bold bg-blue-100 text-blue-700`}>
                                            <Tag className="w-3 h-3 inline mr-1" />{post.tag}
                                        </span>
                                        <span className="flex items-center gap-1 text-slate-400 text-xs">
                                            <Clock className="w-3 h-3" />{post.readTime}
                                        </span>
                                    </div>
                                    <h3 className="text-lg font-bold text-slate-900 mb-3 leading-tight">{post.title}</h3>
                                    <p className="text-slate-500 text-sm leading-relaxed mb-5">{post.summary}</p>
                                    <div className="flex items-center justify-between">
                                        <span className="text-xs text-slate-400">{post.date}</span>
                                        <Link to={`/blog/${post.slug}`} className="flex items-center gap-1 text-blue-600 text-sm font-semibold hover:gap-2 transition-all">
                                            Oku <ArrowRight className="w-4 h-4" />
                                        </Link>
                                    </div>
                                </div>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default Blog;
